<template>
    <div
        role="presentation"
        class="container-placeholder"
        data-cy="empty-list-placeholder"
        :class="{'container-placeholder--shadowed': placeholderShadow}"
    >
        <slot name="icon">
            <img
                v-if="image"
                :height="image.height"
                :width="image.width"
                :src="image.src"
                :alt="image.alt"
                :class="[image.class, {
                    'image-with-shadow': imageWithShadow
                }]"
                class="container-placeholder__image"
            >
        </slot>

        <div class="container-placeholder__description">
            <slot name="title">
                <h3
                    v-if="title"
                    :class="titleClass"
                    class="container-placeholder__title"
                >
                    {{ title }}
                </h3>
            </slot>

            <slot name="subtitle">
                <p
                    v-if="subtitle"
                    class="container-placeholder__subtitle"
                >
                    {{ subtitle }}
                </p>
            </slot>
        </div>

        <slot />
    </div>
</template>

<script lang="ts" setup>
import type { Image } from '@/scripts/types/Image';

withDefaults(
    defineProps<{
        image?: Image,
        title?: string,
        subtitle?: string,
        titleClass?: string|undefined,
        imageWithShadow?: boolean,
        placeholderShadow?: boolean,
    }>(),
    {
        imageWithShadow: false,
        image: undefined,
        title: undefined,
        subtitle: undefined,
        titleClass: undefined,
        placeholderShadow: false,
    },
);
</script>

<style lang="scss" scoped>
@use '@/styles/abstracts/spacings';
@use '@/styles/abstracts/font-sizes';
@use '@/styles/abstracts/line-heights';
@use '@/styles/abstracts/variables';

.container-placeholder {
    display: flex;

    flex-direction: column;

    gap: spacings.$spacing-s;

    align-items: center;
    justify-content: center;

    height: 100%;

    margin: 0 spacings.$spacing-xl spacings.$spacing-xl;

    background-color: var(--theme-color-surface-primary-default);

    border-radius: variables.$border-radius;

    &__description {
        display: flex;

        flex-direction: column;

        gap: spacings.$spacing-xxxs;

        align-items: center;
        justify-content: center;
    }

    &__title {
        margin-bottom: 0;

        font-size: font-sizes.$font-size-lg;
        font-weight: bold;
        line-height: line-heights.$line-height-lg;

        color: var(--theme-color-text-primary);
    }

    &__subtitle {
        max-width: 18rem;

        font-size: font-sizes.$font-size-lg;
        color: var(--theme-color-text-primary);
        text-align: center;
    }

    &__image {
        display: block;
    }

    &--shadowed {
        box-shadow: var(--shadow-placeholder);
    }
}

.image-with-shadow {
    padding: spacings.$spacing-xxs;

    border-radius: variables.$border-radius;

    box-shadow: var(--shadow-placeholder);
}
</style>
